import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { BaseService } from '../base.service';
import { Helper } from '../helper.service';
import { IAuditHistoryEntry, IAuditHistoryFilterOptions, IAuditHistoryQuery } from '../../models/history/audit-history.model';

@Injectable({
  providedIn: 'root',
})
export class AuditHistoryApiService extends BaseService {
  constructor(
    private http: HttpClient,
    private helperService: Helper,
  ) {
    super();
  }

  /**
   * One page of a node's history, newest first.
   *
   * `projectId` is required by the API: it scopes the node:read permission check, and the backend
   * additionally verifies the node really is in that project.
   */
  public getNodeHistory(nodeId: string, projectId: string, pageNumber: number, pageSize: number, query?: IAuditHistoryQuery | null): Observable<IAuditHistoryEntry[]> {
    // No request at all without both ids - the API would only 400, and the History tab is meant to
    // cost nothing until it has something real to ask for.
    if (!nodeId || !projectId) {
      return of([]);
    }

    const url = `${environment.auditHistoryServiceEndPoint}/node/${encodeURIComponent(nodeId)}` + `?${this.buildParams(projectId, pageNumber, pageSize, query)}`;

    return this.http
      .get(url, {
        headers: this.helperService.getApiAuthHeader(),
        responseType: 'json',
      })
      .pipe(
        // Deliberately not coerced to [] the way a list endpoint usually would be. "No history
        // recorded for this item" is a positive claim about the audit record, so a body that is not
        // a list has to surface as a failure the user can retry rather than as an empty trail.
        map((res: unknown) => {
          if (!Array.isArray(res)) {
            throw new Error('History response was not a list.');
          }
          return res as IAuditHistoryEntry[];
        }),
        catchError((err) => this.helperService.handleError(err)),
      );
  }

  /**
   * The same history as a CSV file, unpaged.
   *
   * Server-side rather than built from what the table happens to have loaded: an export of the first
   * 25 of 300 entries would look complete and be wrong, which for an audit trail is worse than no
   * export. Mirrors the Logs tab's export (`CatchHubLogService.exportCatchHubLogs`), including
   * `observe: 'response'` so the caller can read the filename out of Content-Disposition.
   *
   * `responseType: 'text'` rather than `'blob'`: the file's "When (UTC)" column has to become local
   * time before it is saved (see `convertAuditHistoryExportToLocalTime`), and text is the only
   * responseType that hands the caller the CSV as a string it can rewrite synchronously.
   *
   * `isTestNode` is not a filter - it does not change which rows come back, only whether the file's
   * Run label/Step columns exist at all. Only a Test can ever have a step table or per-label runs
   * (see EditPanelHistoryPaneComponent), so those columns are meaningless - all blank - on anything
   * else and are left out of the file entirely rather than shipped empty.
   *
   * `currentLabelId` is the same kind of column toggle: the label the panel is open under, so the
   * server can drop the Label column when nothing in the whole (unpaged) trail was recorded under a
   * different label - not just when a label filter narrowed every row to one on its own.
   */
  public exportNodeHistory(nodeId: string, projectId: string, query?: IAuditHistoryQuery | null, isTestNode = false, currentLabelId: string | null = null): Observable<HttpResponse<string>> | null {
    // Same guard as the two read methods: without both ids the URL would be `.../node//export`,
    // which comes back as an export error and reads as "the download failed" rather than "there was
    // nothing to download".
    if (!nodeId || !projectId) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('projectId', projectId);
    params.set('isTestNode', String(!!isTestNode));
    if (currentLabelId) {
      params.set('currentLabelId', currentLabelId);
    }
    this.appendQuery(params, query);

    const url = `${environment.auditHistoryServiceEndPoint}/node/${encodeURIComponent(nodeId)}/export` + `?${params.toString()}`;

    return this.http
      .get(url, {
        headers: this.helperService.getApiAuthHeader(),
        responseType: 'text',
        observe: 'response',
      })
      .pipe(catchError((err) => this.helperService.handleError(err)));
  }

  /** How many entries the same filter would return. */
  public getNodeHistoryCount(nodeId: string, projectId: string, query?: IAuditHistoryQuery | null): Observable<number> {
    if (!nodeId || !projectId) {
      return of(0);
    }

    const params = new URLSearchParams();
    params.set('projectId', projectId);
    this.appendQuery(params, query);

    const url = `${environment.auditHistoryServiceEndPoint}/node/${encodeURIComponent(nodeId)}/count` + `?${params.toString()}`;

    return this.http
      .get(url, {
        headers: this.helperService.getApiAuthHeader(),
        responseType: 'json',
      })
      .pipe(
        map((res: unknown) => (typeof res === 'number' ? res : 0)),
        catchError((err) => this.helperService.handleError(err)),
      );
  }

  /**
   * The options this item's filter dropdowns should offer - who has changed it, and in which labels.
   *
   * An empty result when there is nothing to ask for yet (no node/project id) - the tab shows "All
   * users" with nothing to pick, the same as a brand-new item. A real request failure is rethrown
   * like every sibling call here instead: swallowing it would render the same "nothing to pick" state
   * as a brand-new item, with no toast and no way to retell the two apart.
   */
  public getFilterOptions(nodeId: string, projectId: string): Observable<IAuditHistoryFilterOptions> {
    const empty: IAuditHistoryFilterOptions = { actors: [], labels: [], actions: [] };

    if (!nodeId || !projectId) {
      return of(empty);
    }

    const url =
      `${environment.auditHistoryServiceEndPoint}/node/${encodeURIComponent(nodeId)}/filter-options` +
      `?projectId=${encodeURIComponent(projectId)}`;

    return this.http
      .get(url, {
        headers: this.helperService.getApiAuthHeader(),
        responseType: 'json',
      })
      .pipe(
        map((res: unknown) => {
          const body = res as Partial<IAuditHistoryFilterOptions> | null;
          return {
            actors: Array.isArray(body?.actors) ? body!.actors : [],
            labels: Array.isArray(body?.labels) ? body!.labels : [],
            actions: Array.isArray(body?.actions) ? body!.actions : [],
          };
        }),
        catchError((err) => this.helperService.handleError(err)),
      );
  }

  private buildParams(projectId: string, pageNumber: number, pageSize: number, query?: IAuditHistoryQuery | null): string {
    const params = new URLSearchParams();
    params.set('projectId', projectId);
    params.set('pageNumber', String(pageNumber));
    params.set('pageSize', String(pageSize));
    this.appendQuery(params, query);
    return params.toString();
  }

  /**
   * Only set filters that are actually in use. Sending `scope=` empty would be rejected by the API,
   * which refuses unknown scope values rather than silently ignoring them.
   */
  private appendQuery(params: URLSearchParams, query?: IAuditHistoryQuery | null): void {
    if (!query) {
      return;
    }
    if (query.scope) {
      params.set('scope', query.scope);
    }
    // Repeated keys, like `actions` below - the API binds each as a string[].
    query.actorUserIds?.filter((id) => !!id).forEach((id) => params.append('actorUserId', id));
    query.labelIds?.filter((id) => !!id).forEach((id) => params.append('labelId', id));
    // Trimmed, and dropped when that leaves nothing: a box holding only spaces is not a filter, and
    // sending one would make the count and the export disagree with the list about what was asked.
    if (query.searchText && query.searchText.trim()) {
      params.set('searchText', query.searchText.trim());
    }
    if (query.fromDate) {
      params.set('fromDate', query.fromDate);
    }
    if (query.toDate) {
      params.set('toDate', query.toDate);
    }
    // Repeated rather than comma-joined: [FromQuery] string[] on the API binds a repeated key
    // ("actions=Created&actions=Updated"), the same convention ASP.NET Core model binding expects
    // for an array parameter.
    if (query.actions?.length) {
      query.actions.forEach((action) => params.append('actions', action));
    }
  }
}
